import { useCallback, useMemo } from "react";
import { useStore } from "react-redux";
import { useAppSelector, useAppDispatch, type RootState } from "../../../store";
import { setImpersonationWatchlistItems, setWatchlistItems } from "../../../reducers/watchlist";
import {
  useRemoveWatchlistItemMutation,
  useUpdateWatchlistMutation,
} from "../../../services/ActionReports/WatchlistBff";
import type { ActionReportRow } from "../common/ActionReportListView/types";
import { padAff } from "../../Authentication/impersonation-util";
import { logger } from "../../../common/util/logger";

export interface UseWatchlistToggleParams {
  row: ActionReportRow;
}

export interface UseWatchlistToggleReturn {
  isInWatchlist: boolean;
  toggleWatchlist: () => Promise<void>;
}

export interface WatchlistTarget {
  aff: string;
  aboNumber: string;
  countryCode?: string;
}

export const useWatchlistItemToggle = () => {
  const dispatch = useAppDispatch();
  const reduxStore = useStore<RootState>();
  const [updateWatchlist] = useUpdateWatchlistMutation();
  const [removeWatchlistItem] = useRemoveWatchlistItemMutation();

  return useCallback(
    async (target: WatchlistTarget): Promise<void> => {
      const state = reduxStore.getState();
      const { boot, watchlist } = state;
      const isLocalOnlyImpersonation =
        boot.isImpersonation && boot.configuration.impersonation?.disableWatchlistMutations === true;
      const activeItems =
        isLocalOnlyImpersonation && watchlist.impersonationInitialized
          ? watchlist.impersonationItems
          : watchlist.watchlistItems;
      const isInWatchlist = activeItems.some((item) => item.ABONo === target.aboNumber && +item.Aff === +target.aff);
      const previousItems = [...activeItems];
      const updatedItems = isInWatchlist
        ? activeItems.filter((item) => !(item.ABONo === target.aboNumber && +item.Aff === +target.aff))
        : [
            ...activeItems,
            {
              Aff: padAff(target.aff),
              ABONo: target.aboNumber,
              ...(target.countryCode ? { IsoCountry: target.countryCode } : {}),
            },
          ];

      if (isLocalOnlyImpersonation) {
        dispatch(setImpersonationWatchlistItems(updatedItems));
        return;
      }

      dispatch(setWatchlistItems(updatedItems));

      try {
        if (isInWatchlist) {
          await removeWatchlistItem({
            ownerAff: boot.user.aff,
            ownerAbo: boot.user.abo,
            targetAff: padAff(target.aff),
            targetAbo: target.aboNumber,
            watchList: previousItems,
          }).unwrap();
        } else {
          await updateWatchlist({ ownerAff: boot.user.aff, ownerAbo: boot.user.abo, watchList: updatedItems }).unwrap();
        }
      } catch (error) {
        const currentBoot = reduxStore.getState().boot;
        if (
          currentBoot.isImpersonation &&
          currentBoot.configuration.impersonation?.disableWatchlistMutations === true
        ) {
          dispatch(setImpersonationWatchlistItems(updatedItems));
          return;
        }

        dispatch(setWatchlistItems(previousItems));
        logger.error("Watchlist toggle failed:", error);
        throw error;
      }
    },
    [dispatch, reduxStore, removeWatchlistItem, updateWatchlist],
  );
};

export const useWatchlistToggle = ({ row }: UseWatchlistToggleParams): UseWatchlistToggleReturn => {
  const { watchlistItems, impersonationItems, impersonationInitialized } = useAppSelector((state) => state.watchlist);
  const { isImpersonation, configuration } = useAppSelector((state) => state.boot);
  const toggleWatchlistItem = useWatchlistItemToggle();
  const isLocalOnlyImpersonation = isImpersonation && configuration.impersonation?.disableWatchlistMutations === true;
  const activeItems = isLocalOnlyImpersonation && impersonationInitialized ? impersonationItems : watchlistItems;

  const computedIsInWatchlist = useMemo(
    () => activeItems.some((item) => item.ABONo === row.aboNumber && +item.Aff === +row.aff),
    [activeItems, row.aboNumber, row.aff],
  );
  const isInWatchlist = computedIsInWatchlist;

  const toggleWatchlist = useCallback(
    async (): Promise<void> =>
      toggleWatchlistItem({ aff: row.aff, aboNumber: row.aboNumber, countryCode: row.countryCode }),
    [row.aboNumber, row.aff, row.countryCode, toggleWatchlistItem],
  );

  return { isInWatchlist, toggleWatchlist };
};
